import React, { useEffect, useState, useContext } from 'react';
import { useParams, useHistory } from 'react-router-dom';
import AuthContext from 'context/AuthProvider';
import {
    Button,
    Box,
    Chip,
    LinearProgress,
    Typography,
    IconButton,
    Checkbox,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    TextField,
    Tabs,
    Tab,
    Grid,
    Paper
} from '@mui/material';
import Switch from '@mui/material/Switch';
import Select from 'react-select';
import ShowToast from 'components/ShowToast';
import { TYPE_SUCCESS, TYPE_FAIL, INTERVAL, DESIGN_ENGINEER } from 'assets/constants/Constants';
import { HTTP_HEADER_KEYS, HTTP_CONTENT_TYPES } from 'assets/constants/apiConstants';
import {
    Save as SaveIcon,
    ArrowBack as ArrowBackIcon,
    CloudUpload as CloudUploadIcon,
    DeleteForever as DeleteForeverIcon,
    AttachFile as AttachFileIcon,
    Add as AddIcon,
    AddCircle as AddCircleIcon,
    CheckCircleSharp as CheckCircleSharpIcon
} from '@mui/icons-material';
import { FormatListNumberedIcon, AddBoxIcon } from 'assets/constants/Icons';

import {
    createEnquiry,
    getEnquiryById,
    updateEnquiry,
    getCustomerList,
    getRegionList,
    getBusinessVerticalList,
    getS3PresignedUrl,
    searchPartsByDocNo,
    getPartList,
    createCustomer,
    getMetaDataCall,
    getUserByRole
} from 'lib/axiosUtils';

import '../BOM/bom.css';
import FeasibilityStudyCard from './components/FeasibilityStudyCard';
import OfferDetailsSection from './components/OfferDetailsSection';
import MaterialTable from 'material-table';
import { ThemeProvider, createTheme } from '@mui/material/styles';
import CreatableSelect from 'react-select/creatable';
import AsyncSelect from 'react-select/async';
import AsyncCreatableSelect from 'react-select/async-creatable';

export const COMPONENT_TYPES = [
    { value: 'Shaft type', label: 'Shaft type' },
    { value: 'Bore Type', label: 'Bore Type' }
];

export const FIXTURE_TYPES = [
    { value: 'Full Fixture – Cylindrical', label: 'Full Fixture – Cylindrical' },
    { value: 'Change parts – Cylindrical', label: 'Change parts – Cylindrical' },
    { value: 'Full Fixture – Bevel', label: 'Full Fixture – Bevel' },
    { value: 'Change parts – Bevel', label: 'Change parts – Bevel' },
    { value: 'Special', label: 'Special' }
];

export const FIXTURE_CONCEPTS = [
    { value: 'Collet Type', label: 'Collet Type' },
    { value: 'Friction Type', label: 'Friction Type' },
    { value: 'Expander Type', label: 'Expander Type' },
    { value: 'Double Collet Type', label: 'Double Collet Type' },
    { value: 'Special', label: 'Special' }
];

function useDebounced(fn, delay = 300) {
    const t = React.useRef();
    return React.useCallback(
        (...args) => {
            clearTimeout(t.current);
            return new Promise((resolve) => {
                t.current = setTimeout(async () => {
                    resolve(await fn(...args));
                }, delay);
            });
        },
        [fn, delay]
    );
}

const emptyStudySchema = {
    feasibilityDescription: '',
    allRequirementsFulfilled: false,
    isComplete: false
};

const theme = createTheme({
    palette: { primary: { main: '#2d3d42' } },
});

const emptyItemSchema = {
    component_drawing_no: '',
    clamping_dia: '',
    machine: '',
    quantity: 1,
    feasibilityStudy: { ...emptyStudySchema },
    localOffer: {
        size: '',
        calculatedPrice: 0,
        isSelected: true
    },
    importedOffer: {
        pos: '',
        materialDescription: '',
        pricePerUnit: 0,
        netValue: 0,
        customTariff: '',
        isSelected: true
    },
    pricingDetails: {
        rmCost: 0, manufacturingCost: 0, boughtOutItems: 0, reworkCost: 0, 
        colletRubberisation: 0, springsAndAccessories: 0, transportCosts: 0, packingCost: 0, 
        designCost: 0, testingCharges: 0, markup: 25, totalBuyingCost: 0, baseOfferPrice: 0, finalOfferPrice: 0, 
        discountedPrice: 0, actualMargin: 0
    }
};

const CreateEnquiryPage = (props) => {
    const { id: routeEnquiryId } = useParams();
    const history = useHistory();

    const rawFetchPartsByDocNo = React.useCallback(
        async (inputValue) => {
            const q = (inputValue || '').trim().toLowerCase();
            try {
                const res = await searchPartsByDocNo({ q, search: q }, {});
                const list = res?.data?.result || [];
                return list.map(p => ({
                    value: p._id,
                    label: p.document_number || `(No Doc#) ${p.part_name}`,
                    part_name: p.part_name || 'Unnamed',
                    part_ref: p._id
                }));
            } catch {
                return [];
            }
        },
        []
    );

    const rawFetchParts = React.useCallback(
        async (inputValue) => {
            const q = (inputValue || '').trim().toLowerCase();
            try {
                const res = await getPartList({ q, search: q, name: q }, {});
                const list = res?.data?.result || [];
                return list.map(p => ({
                    value: p._id,
                    label: p.part_name || 'Unnamed',
                    document_number: p.document_number || '',
                    part_ref: p._id
                }));
            } catch {
                return [];
            }
        },
        []
    );

    const loadDocOptions = useDebounced(rawFetchPartsByDocNo, 500);
    const loadPartOptions = useDebounced(rawFetchParts, 500);

    // Dropdowns
    const [customers, setCustomers] = useState([]);
    const [regions, setRegions] = useState([]);
    const [componentOptions, setComponentOptions] = useState([]);
    const [businessVerticals, setBusinessVerticals] = useState([]);
    const [designEngineers, setDesignEngineers] = useState([]);

    // Form State
    const [enquiry, setEnquiry] = useState({
        customer: null,
        region: null,
        businessVertical: null,
        designEngineerId: null,
        verificationComment: '',
        offerRequirement: { value: 'Detailed', label: 'Detailed' },
        targetDate: '',
        
        machineManufacturer: null,
        machineModel: '',
        machineSrNo: '',
        componentDrawingNumbers: [],
        componentVolumePerMonth: '',
        componentStage: null,
        fullFixture: false,
        changeParts: false,
        expectedFixtureConcept: null,
        specialNeedRemark: '',
        componentManufacturingProcess: '',
        cuttingToolMaxDiameter: '',
        cuttingToolMinDiameter: '',
        hydraulicLeadthrough: { value: 'None', label: 'None' },
        fixtureCentering: null,
        tableAdaptations: '',
        machineAcceptancePAC: false,
        machineAcceptanceFAC: false,
        machineAcceptanceNone: false,
        commonisationRequired: false,
        airSensingRequired: false,
        chipFlushingRequired: false,
        topAssemblyRequired: false,
        productionStudyAvailable: false,
        processDrawingsAvailable: false,
        additionalComments: '',
        items: [],
        offerDetails: {
            offerType: 'None',
            currency: 'INR',
            localOffer: { profitMargin: 0, negotiationMargin: 0, suggestedReferencePrice: 0, finalOfferValue: 0 },
            importedOffer: { klQuotationNumber: '', freightCost: 0, duties: 0, localTransport: 0, margin: 0, negotiationMargin: 0, discountScope: 0, finalLandedPrice: 0 }
        },
        totalPricingSummary: {
            totalRM: 0, totalMfg: 0, totalBoughtOut: 0, totalRework: 0, totalCollet: 0, totalSprings: 0,
            totalTransport: 0, totalPacking: 0, totalDesign: 0, totalTesting: 0,
            aggregateTotalBuyingCost: 0, aggregateBaseOfferPrice: 0, aggregateFinalOfferPrice: 0,
            aggregateDiscountedPrice: 0, aggregateActualMargin: 0
        },
        attachments: {
            partDrawings: [],
            fixtureSampleLayouts: [],
            customerEnquiryEmail: [],
            clampingConceptCutouts: []
        }
    });

    const [machineDialogOpen, setMachineDialogOpen] = useState(false);
    const [newMachine, setNewMachine] = useState({ manufacturer: '', model: '' });

    const getMinTargetDate = () => {
        const minDate = new Date();
        minDate.setDate(minDate.getDate() + 7);
        return minDate.toISOString().split('T')[0];
    };

    // File draft states
    const [partDrawingsDraft, setPartDrawingsDraft] = useState([]);
    const [fixtureSampleLayoutsDraft, setFixtureSampleLayoutsDraft] = useState([]);
    const [customerEnquiryEmailDraft, setCustomerEnquiryEmailDraft] = useState([]);
    const [clampingConceptCutoutsDraft, setClampingConceptCutoutsDraft] = useState([]);
    const [isUploading, setIsUploading] = useState(false);
    const [uploadPct, setUploadPct] = useState(0);

    const [isSaving, setIsSaving] = useState(false);
    const isEditMode = Boolean(routeEnquiryId);

    const [feasibilityConfigurations, setFeasibilityConfigurations] = useState([]);
    const [quotationMetadata, setQuotationMetadata] = useState(null);

    const [addPartDialogOpen, setAddPartDialogOpen] = useState(false);
    const [newPartData, setNewPartData] = useState({ 
        component_drawing_no: [], 
        clamping_dia: '', 
        quantity: 1, 
        part_ref: null,
        component_type: null,
        fixture_concept: null,
        fixture_type: null
    });

    const [pendingNextStage, setPendingNextStage] = useState(null);
    const [isCommentDialogOpen, setIsCommentDialogOpen] = useState(false);
    const [approvalComment, setApprovalComment] = useState("");


    // Temporary Customer States
    const [tempCustDialogOpen, setTempCustDialogOpen] = useState(false);
    const [tempCustomers, setTempCustomers] = useState([]);
    const [tempTabValue, setTempTabValue] = useState(0); // 0 for select existing, 1 for add new
    const [isSavingTempCust, setIsSavingTempCust] = useState(false);
    const [newTempCust, setNewTempCust] = useState({
        customer_name: '',
        full_address: '',
        pin_code: '',
        contact_name: '',
        designation: '',
        phone_number: '',
        email: '',
        temporary_customer: true
    });

    useEffect(() => {
        loadDropdowns();
        if (isEditMode) {
            fetchEnquiryDetails(routeEnquiryId);
        }
    }, [routeEnquiryId, isEditMode]);

    const loadDropdowns = async () => {
        try {   
            const [custRes, regRes, vertRes, metaRes, deRes] = await Promise.all([
                getCustomerList({}, {}),
                getRegionList({}, {}),
                getBusinessVerticalList({}, {}),
                getMetaDataCall({}, {}),
                getUserByRole({}, { role: DESIGN_ENGINEER })
            ]);
            
            if (custRes?.status === 'SUCCESS') setCustomers(custRes.data.result || []);
            if (regRes?.status === 'SUCCESS') setRegions(regRes.data.result || []);
            if (vertRes?.status === 'SUCCESS') setBusinessVerticals(vertRes.data.result || []);
            if (deRes?.status === 'SUCCESS') setDesignEngineers(deRes.data.result || []);
            
            if (metaRes?.status === 'SUCCESS' && metaRes.data?.result?.length > 0) {
                const metadata = metaRes.data.result[0];
                if (metadata.meta_data && metadata.meta_data.feasibility_settings) {
                    const settings = metadata.meta_data.feasibility_settings;
                    if (settings.configurations) {
                        setFeasibilityConfigurations(settings.configurations);
                    }
                }
                if (metadata.meta_data && metadata.meta_data.quotationMetadata) {
                    setQuotationMetadata(metadata.meta_data.quotationMetadata);
                }
            }
        } catch (error) {
            console.error('Error loading dropdowns:', error);
            ShowToast('Failed to load form options', TYPE_FAIL, INTERVAL);
        }
    };

    const fetchTempCustomers = async () => {
        try {
            const res = await getCustomerList({}, { temporary_customer: 'true' });
            if (res?.status === 'SUCCESS') {
                setTempCustomers(res.data.result || []);
            }
        } catch (error) {
            console.error('Error fetching temp customers:', error);
        }
    };

    const handleOpenTempCustDialog = () => {
        setTempCustDialogOpen(true);
        setTempTabValue(0);
        fetchTempCustomers();
    };

    const handleCloseTempCustDialog = () => {
        setTempCustDialogOpen(false);
    };

    const handleSaveNewMachine = () => {
        if (!newMachine.manufacturer || !newMachine.model) {
            ShowToast('Please enter both manufacturer and model', TYPE_FAIL, INTERVAL, props, '');
            return;
        }
        const label = `${newMachine.manufacturer} - ${newMachine.model}`;
        handleSelectChange('machineManufacturer', { value: label, label });
        setEnquiry(prev => ({ ...prev, machineModel: newMachine.model }));
        setMachineDialogOpen(false);
        setNewMachine({ manufacturer: '', model: '' });
    };

    const handleSaveNewTempCust = async () => {
        if (!newTempCust.customer_name || !newTempCust.full_address || !newTempCust.contact_name || !newTempCust.email) {
            ShowToast('Please fill all compulsory fields', TYPE_FAIL, INTERVAL);
            return;
        }
        setIsSavingTempCust(true);
        try {
            const res = await createCustomer({}, newTempCust);
            if (res?.status === 201 || res?.statusCode === 201) {
                const created = res.data.result;
                ShowToast('Temporary customer created', TYPE_SUCCESS, INTERVAL);
                setEnquiry(prev => ({
                    ...prev,
                    customer: { value: created._id, label: created.customer_name }
                }));
                handleCloseTempCustDialog();
                loadDropdowns();
            } else {
                ShowToast(res?.data?.error || 'Failed to create customer', TYPE_FAIL, INTERVAL);
            }
        } catch (error) {
            console.error('Error creating temp customer:', error);
            ShowToast('Error creating temporary customer', TYPE_FAIL, INTERVAL);
        } finally {
            setIsSavingTempCust(false);
        }
    };

    const fetchEnquiryDetails = async (id) => {
        try {
            const resp = await getEnquiryById({}, {}, id);
            if (resp.status === 'SUCCESS' && resp.data.result) {
                const data = resp.data.result;
                const componentNumbers = data.componentDrawingNumbers || [];
                console.log("Component Numbers:", data.componentDrawingNumbers);
                setComponentOptions(
                    (data.componentDrawingNumbers || []).map(item => ({
                    value: item,
                    label: item
                }))
            );
                
                let normalizedItems = [];
                if (Array.isArray(data.items) && data.items.length > 0) {
                    normalizedItems = data.items.map(item => ({ 
                        ...emptyItemSchema, 
                        ...item, 
                        feasibilityStudy: { ...emptyStudySchema, ...item.feasibilityStudy },
                        localOffer: { ...emptyItemSchema.localOffer, ...item.localOffer },
                        importedOffer: { ...emptyItemSchema.importedOffer, ...item.importedOffer },
                        pricingDetails: { ...emptyItemSchema.pricingDetails, ...item.pricingDetails }
                    }));
                } else if (Array.isArray(data.feasibilityStudies) && data.feasibilityStudies.length > 0) {
                    normalizedItems = data.feasibilityStudies.map(s => ({ ...emptyItemSchema, feasibilityStudy: { ...emptyStudySchema, ...s } }));
                } else if (data.feasibilityStudy && typeof data.feasibilityStudy === 'object') {
                    normalizedItems = [{ ...emptyItemSchema, feasibilityStudy: { ...emptyStudySchema, ...data.feasibilityStudy } }];
                } else {
                    normalizedItems = [];
                }

                if (data.offerDetails?.localOffer?.lineItems?.length > 0 && (!data.items || data.items.length === 0)) {
                    data.offerDetails.localOffer.lineItems.forEach((li, idx) => {
                        if (normalizedItems[idx]) {
                            normalizedItems[idx].localOffer = { ...emptyItemSchema.localOffer, ...li, size: li.size || '' };
                            if (!normalizedItems[idx].part_name && li.partName) normalizedItems[idx].part_name = li.partName;
                        } else {
                            normalizedItems.push({ ...emptyItemSchema, part_name: li.partName, localOffer: { ...emptyItemSchema.localOffer, ...li, size: li.size || '' } });
                        }
                    });
                }
                
                if (data.offerDetails?.importedOffer?.klLineItems?.length > 0 && (!data.items || data.items.length === 0)) {
                    data.offerDetails.importedOffer.klLineItems.forEach((li, idx) => {
                        if (normalizedItems[idx]) {
                            normalizedItems[idx].importedOffer = { ...emptyItemSchema.importedOffer, ...li, pos: li.pos || '' };
                            if (!normalizedItems[idx].part_name && li.materialDescription) normalizedItems[idx].part_name = li.materialDescription;
                        } else {
                            normalizedItems.push({ ...emptyItemSchema, part_name: li.materialDescription, quantity: li.quantity || 1, importedOffer: { ...emptyItemSchema.importedOffer, ...li, pos: li.pos || '' } });
                        }
                    });
                }

                setEnquiry({
                    ...data,
                    customer: data.customer ? { 
                        value: data.customer._id || data.customer, 
                        label: data.customer.customer_name || 'Unknown',
                        ...(typeof data.customer === 'object' ? data.customer : {})
                    } : null,
                    region: data.region ? { value: data.region._id || data.region, label: data.region.name || 'Unknown' } : null,
                    businessVertical: data.businessVertical ? { value: data.businessVertical._id || data.businessVertical, label: data.businessVertical.name || 'Unknown' } : null,
                    designEngineerId: data.designEngineerId ? { 
                        value: data.designEngineerId._id || data.designEngineerId, 
                        label: data.designEngineerId.first_name ? `${data.designEngineerId.first_name} ${data.designEngineerId.last_name}` : 'Unknown' 
                    } : null,
                    verificationComment: data.verificationComment || '',
                    offerRequirement: data.offerRequirement ? { value: data.offerRequirement, label: data.offerRequirement } : null,
                    machineManufacturer: data.machineManufacturer ? { value: data.machineManufacturer, label: data.machineManufacturer } : null,
                    componentStage: data.componentStage ? { value: data.componentStage, label: data.componentStage } : null,
                    expectedFixtureConcept: data.expectedFixtureConcept ? { value: data.expectedFixtureConcept, label: data.expectedFixtureConcept } : null,
                    fixtureCentering: data.fixtureCentering ? { value: data.fixtureCentering, label: data.fixtureCentering } : null,
                    hydraulicLeadthrough: data.hydraulicLeadthrough ? { value: data.hydraulicLeadthrough, label: data.hydraulicLeadthrough } : { value: 'None', label: 'None' },
                    targetDate: data.targetDate ? data.targetDate.split('T')[0] : '',
                    componentDrawingNumbers: Array.isArray(data.componentDrawingNumbers) 
                        ? data.componentDrawingNumbers.map(dn => ({ value: dn, label: dn }))
                        : (data.componentDrawingNumbers ? [{ value: data.componentDrawingNumbers, label: data.componentDrawingNumbers }] : []),
                    items: normalizedItems,
                    offerDetails: data.offerDetails || {
                        offerType: 'None',
                        currency: 'INR',
                        localOffer: { profitMargin: 0, negotiationMargin: 0, suggestedReferencePrice: 0, finalOfferValue: 0 },
                        importedOffer: { klQuotationNumber: '', freightCost: 0, duties: 0, localTransport: 0, margin: 0, negotiationMargin: 0, discountScope: 0, finalLandedPrice: 0 }
                    },
                    totalPricingSummary: data.totalPricingSummary || {
                        totalRM: 0, totalMfg: 0, totalBoughtOut: 0, totalRework: 0, totalCollet: 0, totalSprings: 0,
                        totalTransport: 0, totalPacking: 0, totalDesign: 0, totalTesting: 0,
                        aggregateTotalBuyingCost: 0, aggregateBaseOfferPrice: 0, aggregateFinalOfferPrice: 0,
                        aggregateDiscountedPrice: 0, aggregateActualMargin: 0
                    },
                    attachments: {
                        partDrawings: data.attachments?.partDrawings || [],
                        fixtureSampleLayouts: data.attachments?.fixtureSampleLayouts || [],
                        customerEnquiryEmail: data.attachments?.customerEnquiryEmail || [],
                        clampingConceptCutouts: data.attachments?.clampingConceptCutouts || [],
                        kldQuotations: data.attachments?.kldQuotations || []
                    }
                });
            } else {
                ShowToast('Failed to load enquiry details', TYPE_FAIL, INTERVAL);
            }
        } catch (error) {
            console.error('Error fetching enquiry:', error);
            ShowToast('Error fetching enquiry details', TYPE_FAIL, INTERVAL);
        }
    };

    const handleChangeText = (e) => {
        const { name, value, type, checked } = e.target;
        setEnquiry(prev => ({
            ...prev,
            [name]: type === 'checkbox' ? checked : value
        }));
    };

    const handleItemFeasibilityChange = (index, e) => {
        const { name, value, type, checked } = e.target;
        setEnquiry(prev => {
            const updated = [...prev.items];
            const updatedStudy = { ...updated[index].feasibilityStudy };
            updatedStudy[name] = type === 'checkbox' ? (checked || false) : (type === 'number' ? Number(value) : value);
            updated[index] = { ...updated[index], feasibilityStudy: updatedStudy };
            return { ...prev, items: updated };
        });
    };

    const handleSelectChange = (name, selectedOption) => {
        if (name === 'customer' && selectedOption?.value === 'ADD_TEMP') {
            handleOpenTempCustDialog();
            return;
        }
        setEnquiry(prev => ({
            ...prev,
            [name]: selectedOption
        }));
    };

    const addFilesToDraft = (category, files) => {
        if (!files?.length) return;
        const fileList = Array.from(files);
        switch (category) {
            case 'partDrawings':
                setPartDrawingsDraft(prev => [...prev, ...fileList]);
                break;
            case 'fixtureSampleLayouts':
                setFixtureSampleLayoutsDraft(prev => [...prev, ...fileList]);
                break;
            case 'customerEnquiryEmail':
                setCustomerEnquiryEmailDraft(prev => [...prev, ...fileList]);
                break;
            case 'clampingConceptCutouts':
                setClampingConceptCutoutsDraft(prev => [...prev, ...fileList]);
                break;
            default:
                break;
        }
    };
    const handleFileSelect = (category, e) => {
        addFilesToDraft(category, e.target.files);
        e.target.value = '';
    };
    const handleFileDrop = (category, e) => {
        e.preventDefault();
        e.stopPropagation();
        addFilesToDraft(category, e.dataTransfer.files);
    };
    const handleDragOver = (e) => {
        e.preventDefault();
        e.stopPropagation();
    };
    
    const removeDraftFile = (category, index) => {
        switch (category) {
            case 'partDrawings': setPartDrawingsDraft(prev => prev.filter((_, i) => i !== index)); break;
            case 'fixtureSampleLayouts': setFixtureSampleLayoutsDraft(prev => prev.filter((_, i) => i !== index)); break;
            case 'customerEnquiryEmail': setCustomerEnquiryEmailDraft(prev => prev.filter((_, i) => i !== index)); break;
            case 'clampingConceptCutouts': setClampingConceptCutoutsDraft(prev => prev.filter((_, i) => i !== index)); break;
        }
    };

    const removeExistingFile = (category, index) => {
        setEnquiry(prev => {
            const updated = { ...prev };
            const arr = [...(updated.attachments?.[category] || [])];
            arr.splice(index, 1);
            updated.attachments = { ...updated.attachments, [category]: arr };
            return updated;
        });
    };

    const handleOpenAddPartDialog = () => {
        setNewPartData({ 
            component_drawing_no: [], 
            clamping_dia: '', 
            quantity: 1, 
            part_ref: null,
            component_type: null,
            fixture_concept: null,
            fixture_type: null
        });
        setAddPartDialogOpen(true);
    };

    const handleCloseAddPartDialog = () => {
        setAddPartDialogOpen(false);
    };

    const getFilteredFixtureTypes = () => {
        const vertical = enquiry.businessVertical?.label || '';
        const isSpecialConcept = newPartData.fixture_concept?.value === 'Special';
        
        const options = [];
        
        if (vertical.includes('CGWH') || vertical.includes('ESM')) {
            options.push(
                { value: 'Full Fixture – Cylindrical', label: 'Full Fixture – Cylindrical' },
                { value: 'Change parts – Cylindrical', label: 'Change parts – Cylindrical' }
            );
            if (isSpecialConcept) {
                options.push({ value: 'Special', label: 'Special' });
            }
        } else if (vertical.includes('BGWH')) {
            options.push(
                { value: 'Full Fixture – Bevel', label: 'Full Fixture – Bevel' },
                { value: 'Change parts – Bevel', label: 'Change parts – Bevel' }
            );
            if (isSpecialConcept) {
                options.push({ value: 'Special', label: 'Special' });
            }
        } else {
            return FIXTURE_TYPES;
        }
        
        return options;
    };

    const handleSaveNewPart = () => {
        if (!newPartData.component_drawing_no || newPartData.component_drawing_no.length === 0 || !newPartData.component_type || !newPartData.fixture_type) {
            ShowToast('Please fill all compulsory fields (Drawing No, Component Type, Fixture Type)', TYPE_FAIL, INTERVAL);
            return;
        }

        // Find matching configuration from backend
        const config = feasibilityConfigurations.find(c => 
            c.trigger.component_type === newPartData.component_type.value && 
            c.trigger.fixture_type === newPartData.fixture_type.value
        );

        let generatedTableRows = [];
        if (config && config.tables) {
            config.tables.forEach(table => {
                const categoryName = table.table_title ? table.table_title.trim() : 'Part';
                
                if (Array.isArray(table.rows)) {
                    table.rows.forEach(row => {
                        generatedTableRows.push({
                            isChecked: false,
                            category: categoryName,
                            name: row.name || '',
                            diameter: '',
                            length: '',
                            remarks: '',
                            isEditable: row.isEditable
                        });
                    });
                }
            });
        }
        const generatedFixtureName = [newPartData.fixture_concept?.label, newPartData.fixture_type?.label].filter(Boolean).join(' ');
        setEnquiry(prev => ({
            ...prev,
            items: [...prev.items, { 
                ...emptyItemSchema, 
                component_drawing_no: newPartData.component_drawing_no.map(opt => opt.label).join(', '), 
                clamping_dia: newPartData.clamping_dia || '', 
                machine: prev.machineModel || '',
                quantity: newPartData.quantity || 1,
                // Inject the new structured data
                feasibilityStudy: {
                    ...emptyStudySchema,
                    component_type: newPartData.component_type.value,
                    fixture_concept: newPartData.fixture_concept?.value || '',
                    fixture_type: newPartData.fixture_type.value,
                    fixture_name: generatedFixtureName,
                    partsTable: generatedTableRows
                }
            }]
        }));
        setAddPartDialogOpen(false);
    };

    const uploadFilesToS3 = async (files) => {
        if (!files.length) return [];
        const uploaded = [];
        for (let i = 0; i < files.length; i++) {
            const file = files[i];
            const presignedRes = await getS3PresignedUrl({}, {
                fileName: file.name,
                fileType: file.type || 'application/octet-stream'
            });
            const putUrl = presignedRes?.data?.url;
            const publicUrl = presignedRes?.data?.fileUrl;
            if (putUrl) {
                await new Promise((resolve, reject) => {
                    const xhr = new XMLHttpRequest();
                    xhr.open('PUT', putUrl, true);
                    xhr.setRequestHeader(HTTP_HEADER_KEYS.CONTENT_TYPE, file.type || HTTP_CONTENT_TYPES.OCTET_STREAM);
                    xhr.upload.onprogress = (e) => {
                        if (e.lengthComputable) {
                            const pct = Math.round(((i + (e.loaded / e.total)) / files.length) * 100);
                            setUploadPct(pct);
                        }
                    };
                    xhr.onload = () => (xhr.status >= 200 && xhr.status < 300 ? resolve() : reject());
                    xhr.onerror = () => reject();
                    xhr.send(file);
                });

                let file_key = '';
                try {
                    const u = new URL(putUrl);
                    const path = decodeURIComponent(u.pathname.replace(/^\/+/, ''));
                    const at = path.indexOf('uploads/');
                    file_key = at >= 0 ? path.substring(at) : path;
                } catch (e) {}

                uploaded.push({
                    file_name: file.name,
                    file_type: file.type,
                    file_url: publicUrl,
                    file_key: file_key
                });
            }
        }
        return uploaded;
    };

    const handleSave = async (statusOverride = null, comment = null) => {
        if (
            !enquiry.customer ||
            !enquiry.region ||
            !enquiry.offerRequirement ||
            !enquiry.targetDate ||
            !enquiry.machineManufacturer ||
            !enquiry.machineModel?.trim() ||
            !enquiry.componentStage ||
            !enquiry.componentManufacturingProcess?.trim() ||
            !enquiry.hydraulicLeadthrough ||
            !enquiry.fixtureCentering ||
            !enquiry.componentDrawingNumbers?.length ||
            (!enquiry.fullFixture && !enquiry.changeParts) ||
            (
                enquiry.expectedFixtureConcept?.value === 'Special need' &&
                !enquiry.specialNeedRemark?.trim()
            )
        ) {
            ShowToast(
                'Please fill all required compulsory fields before saving',
                TYPE_FAIL,
                INTERVAL
            );
            return;
        }

        setIsSaving(true);
        try {
            const allDrafts = [...partDrawingsDraft, ...fixtureSampleLayoutsDraft, ...customerEnquiryEmailDraft, ...clampingConceptCutoutsDraft];
            let newPartDrawings = [];
            let newFixtureLayouts = [];
            let newEnquiryEmails = [];
            let newClampingCutouts = [];

            if (allDrafts.length > 0) {
                setIsUploading(true);
                try {
                    if (partDrawingsDraft.length > 0) {
                        newPartDrawings = await uploadFilesToS3(partDrawingsDraft);
                    }
                    if (fixtureSampleLayoutsDraft.length > 0) {
                        newFixtureLayouts = await uploadFilesToS3(fixtureSampleLayoutsDraft);
                    }
                    if (customerEnquiryEmailDraft.length > 0) {
                        newEnquiryEmails = await uploadFilesToS3(customerEnquiryEmailDraft);
                    }
                    if (clampingConceptCutoutsDraft.length > 0) {
                        newClampingCutouts = await uploadFilesToS3(clampingConceptCutoutsDraft);
                    }
                } catch (uploadErr) {
                    ShowToast('File upload failed', TYPE_FAIL, INTERVAL);
                    setIsUploading(false);
                    setIsSaving(false);
                    return;
                }
                setIsUploading(false);
                setUploadPct(0);
            }

            const mergedAttachments = {
                partDrawings: [...(enquiry.attachments?.partDrawings || []), ...newPartDrawings],
                fixtureSampleLayouts: [...(enquiry.attachments?.fixtureSampleLayouts || []), ...newFixtureLayouts],
                customerEnquiryEmail: [...(enquiry.attachments?.customerEnquiryEmail || []), ...newEnquiryEmails],
                clampingConceptCutouts: [...(enquiry.attachments?.clampingConceptCutouts || []), ...newClampingCutouts]
            };

            const payload = { 
                ...enquiry,
                customer: enquiry.customer?.value,
                region: enquiry.region?.value,
                businessVertical: enquiry.businessVertical?.value,
                designEngineerId: enquiry.designEngineerId?.value,
                offerRequirement: enquiry.offerRequirement?.value,
                machineManufacturer: enquiry.machineManufacturer?.value,
                componentStage: enquiry.componentStage?.value,
                expectedFixtureConcept: enquiry.expectedFixtureConcept?.value,
                fixtureCentering: enquiry.fixtureCentering?.value,
                hydraulicLeadthrough: enquiry.hydraulicLeadthrough?.value,
                componentDrawingNumbers: Array.isArray(enquiry.componentDrawingNumbers) 
                    ? enquiry.componentDrawingNumbers.map(opt => opt.value)
                    : [],
                attachments: mergedAttachments
            };

            if (statusOverride && typeof statusOverride === 'string') {
                payload.status = statusOverride;
            }

            if (comment && typeof comment === 'string' && comment.trim() !== '') {
                payload.additionalComments = payload.additionalComments ? payload.additionalComments + '\n[' + new Date().toLocaleDateString() + ']: ' + comment : '[' + new Date().toLocaleDateString() + ']: ' + comment;
            }

            if (!payload.machineManufacturer) delete payload.machineManufacturer;
            if (!payload.componentStage) delete payload.componentStage;
            if (!payload.expectedFixtureConcept) delete payload.expectedFixtureConcept;
            if (!payload.fixtureCentering) delete payload.fixtureCentering;
            delete payload.feasibilityStudy;
            delete payload.feasibilityStudies;

            let resp;
            if (isEditMode) {
                resp = await updateEnquiry({}, payload, routeEnquiryId);
            } else {
                resp = await createEnquiry({}, payload);
            }

            if (resp.status === 'SUCCESS' || resp.statusCode === 200 || resp.statusCode === 201) {
                ShowToast(`Enquiry ${isEditMode ? 'updated' : 'created'} successfully`, TYPE_SUCCESS, INTERVAL);
                setPartDrawingsDraft([]);
                setFixtureSampleLayoutsDraft([]);
                setCustomerEnquiryEmailDraft([]);
                setClampingConceptCutoutsDraft([]);
                setEnquiry(prev => ({ ...prev, attachments: mergedAttachments }));
            } else {
                ShowToast(resp.data?.error || 'Failed to save enquiry', TYPE_FAIL, INTERVAL);
            }
        } catch (error) {
            console.error('Save error:', error);
            ShowToast('An error occurred while saving', TYPE_FAIL, INTERVAL);
        } finally {
            setIsSaving(false);
        }
    };

    const onApproveClick = async (e) => {
        e?.preventDefault?.();

        if (!routeEnquiryId) {
            ShowToast('Save the enquiry before approving it.', TYPE_FAIL, INTERVAL);
            return;
        }

        const currentStage = enquiry.status || 'Enquiry Generated';
        let nextStage = '';

        if (currentStage === 'Enquiry Generated') {
            nextStage = 'Verification';
        } else if (currentStage === 'Verification') {
            if (!enquiry.businessVertical) {
                ShowToast('Please select a Business Vertical before approving.', TYPE_FAIL, INTERVAL);
                return;
            }
            nextStage = 'Feasibility Study';
        } else if (currentStage === 'Feasibility Study') {
            nextStage = 'Offer Submitted';
        } else if (currentStage === 'Offer Submitted') {
            nextStage = 'PO Received';
        } else if (currentStage === 'PO Received') {
            nextStage = 'DAP Preparation';
        } else if (currentStage === 'DAP Preparation') {
            nextStage = 'DAP Approved';
        } else if (currentStage === 'DAP Approved') {
            nextStage = 'BOM Preparation';
        } else {
            nextStage = currentStage;
        }

        if (nextStage !== currentStage) {
            setPendingNextStage(nextStage);
            setApprovalComment("");
            setIsCommentDialogOpen(true);
        } else {
            handleSave();
        }
    };

    const handleFinalApproval = async (comment = "") => {
        if (!pendingNextStage) return;
        
        if (window.confirm(`Are you sure you want to approve and move to ${pendingNextStage}?`)) {
            await handleSave(pendingNextStage, comment);
        }
    };

    const renderFieldRow = ({ label, required = false, children, colSpace = "col-md-4" }) => (
        <div className={`${colSpace} mb-3 d-flex flex-column`}>
            <label className="form-label d-block bom-label" style={{ marginBottom: '6px' }}>
                {label} {required && <span className="text-danger ms-1">*</span>}
            </label>
            <div style={{ flex: 1, display: 'flex', alignItems: 'flex-start' }}>
                <div style={{ width: '100%' }}>
                    {children}
                </div>
            </div>
        </div>
    );

    const selectStyles = {
        menuPortal: (base) => ({ ...base, zIndex: 9999 }),
        control: (base) => ({
            ...base,
            fontSize: 'var(--bom-input-font-size, 14px)',
            minHeight: '38px',
            height: '38px',
            borderColor: '#dfe1e5',
            padding: '0px',
            boxShadow: 'none',
        }),
        valueContainer: (base) => ({
            ...base,
            padding: '0px 8px',
        }),
        option: (base) => ({
            ...base,
            fontSize: 'var(--bom-input-font-size, 14px)',
        }),
        singleValue: (base) => ({
            ...base,
            fontSize: 'var(--bom-input-font-size, 14px)',
        }),
        input: (base) => ({
            ...base,
            fontSize: 'var(--bom-input-font-size, 14px)',
            margin: '0px',
        }),
    };


    const renderInput = (p, field, defaultValue = 0) => (
        <input
            type="number"
            className="form-control-input bom-input no-spin"
            style={{ width: '100%', minWidth: '60px', textAlign: 'left', padding: '4px 8px' }}
            onWheel={(e) => e.target.blur()}
            value={p.rowData.pricingDetails?.[field] ?? defaultValue}
            onChange={(e) => {
                const val = Number(e.target.value);
                const pricing = { ...(p.rowData.pricingDetails || emptyItemSchema.pricingDetails), [field]: val };
                
                const tbc = (pricing.rmCost||0) + (pricing.manufacturingCost||0) + (pricing.boughtOutItems||0) + 
                            (pricing.reworkCost||0) + (pricing.colletRubberisation||0) + (pricing.springsAndAccessories||0) + 
                            (pricing.transportCosts||0) + (pricing.packingCost||0);
                
                const currentMarkup = pricing.markup ?? 25;
                const bop = tbc * (1 + currentMarkup / 100);
                
                // Constant 2% for Design and Testing
                const dc = bop * 0.02;
                const tc = bop * 0.02;

                const fop = bop + dc + tc;
                const dp = fop * 0.95;
                let am = 0;
                if (tbc > 0) am = (dp / tbc) - 1;

                pricing.markup = currentMarkup;
                pricing.totalBuyingCost = Number(tbc.toFixed(2));
                pricing.baseOfferPrice = Number(bop.toFixed(2));
                pricing.designCost = Number(dc.toFixed(2));
                pricing.testingCharges = Number(tc.toFixed(2));
                pricing.finalOfferPrice = Number(fop.toFixed(2));
                pricing.discountedPrice = Number(dp.toFixed(2));
                pricing.actualMargin = Number(am.toFixed(4));

                p.onRowDataChange({ ...p.rowData, pricingDetails: pricing });
            }}
        />
    );

    const columnsbase = [
        {
            title: <div style={{ lineHeight: 1.2, whiteSpace: 'nowrap' }}>Component<br/>Drawing No</div>,
            field: 'component_drawing_no',
            width: 150,
            editComponent: (p) => (
                <Select
                    options={enquiry.componentDrawingNumbers || []}
                    value={p.value ? { label: p.value, value: p.value } : null}
                    onChange={(opt) => p.onChange(opt ? opt.label : '')}
                    placeholder="Select Component Drawing No..."
                    isClearable
                    menuPortalTarget={document.body}
                    styles={selectStyles}
                    menuPosition="fixed"
                />
            )
        },
        {
            title: <div style={{ lineHeight: 1.2 }}>Clamping<br/>Dia</div>,
            field: 'clamping_dia',
            width: 100,
            editComponent: (p) => (
                <input
                    type="text"
                    className="form-control-input bom-input"
                    value={p.value ?? ''}
                    onChange={(e) => p.onChange(e.target.value)}
                />
            )
        },
        {
            title: 'Machine',
            field: 'machine',
            width: 100,
            render: (rowData) => rowData.machine || enquiry.machineModel || '',
            editComponent: (p) => {
                // Ensure machine is kept updated silently or manually
                return (
                    <input
                        type="text"
                        className="form-control-input bom-input"
                        value={enquiry.machineModel || ''}
                        disabled
                    />
                )
            }
        },
        {
            title: 'QTY',
            field: 'quantity',
            type: 'numeric',
            align: 'left',
            editComponent: (p) => (
                <input
                    type="number"
                    className="form-control-input bom-input no-spin"
                    style={{ width: '70px', textAlign: 'left' }}
                    onWheel={(e) => e.target.blur()}
                    value={p.value ?? 1}
                    onChange={(e) => p.onChange(Number(e.target.value))}
                />
            )
        },
        {
            title: <div style={{ lineHeight: 1.2 }}>Feasibility<br/>Status</div>,
            field: 'feasibility_status',
            editable: 'never',
            align: 'left',
            render: (rowData) => {
                const isComplete = rowData.feasibilityStudy?.isComplete;
                return (
                    <Chip 
                        label={isComplete ? "Complete" : "Pending"} 
                        size="small" 
                        color={isComplete ? "success" : "warning"} 
                    />
                );
            }
        }
    ];

    let columns = [];
    if (enquiry.status === 'Feasibility Study') {
        columns = columnsbase.filter(c => ['component_drawing_no', 'clamping_dia', 'machine', 'quantity', 'feasibility_status'].includes(c.field));
    } else {
        columns = [
            ...columnsbase,
            { title: <div style={{ lineHeight: 1.2 }}>RM<br/>Cost</div>, field: 'pricingDetails.rmCost', type: 'numeric', align: 'left', editComponent: p => renderInput(p, 'rmCost') },
            { title: <div style={{ lineHeight: 1.2 }}>Mfg<br/>Cost</div>, field: 'pricingDetails.manufacturingCost', type: 'numeric', align: 'left', editComponent: p => renderInput(p, 'manufacturingCost') },
            { title: <div style={{ lineHeight: 1.2 }}>Buy<br/>Out</div>, field: 'pricingDetails.boughtOutItems', type: 'numeric', align: 'left', editComponent: p => renderInput(p, 'boughtOutItems') },
            { title: 'Rework', field: 'pricingDetails.reworkCost', type: 'numeric', align: 'left', editComponent: p => renderInput(p, 'reworkCost') },
            { title: 'Collet', field: 'pricingDetails.colletRubberisation', type: 'numeric', align: 'left', editComponent: p => renderInput(p, 'colletRubberisation') },
            { title: 'Springs', field: 'pricingDetails.springsAndAccessories', type: 'numeric', align: 'left', editComponent: p => renderInput(p, 'springsAndAccessories') },
            { title: 'Transport', field: 'pricingDetails.transportCosts', type: 'numeric', align: 'left', editComponent: p => renderInput(p, 'transportCosts') },
            { title: 'Packing', field: 'pricingDetails.packingCost', type: 'numeric', align: 'left', editComponent: p => renderInput(p, 'packingCost') },
            { title: <div style={{ lineHeight: 1.2 }}>Total<br/>Buying</div>, field: 'pricingDetails.totalBuyingCost', type: 'numeric', align: 'left', editable: 'never' },
            { title: 'Design', field: 'pricingDetails.designCost', type: 'numeric', align: 'left', editable: 'never' },
            { title: 'Testing', field: 'pricingDetails.testingCharges', type: 'numeric', align: 'left', editable: 'never' },
            { title: <div style={{ lineHeight: 1.2 }}>Base<br/>Offer</div>, field: 'pricingDetails.baseOfferPrice', type: 'numeric', align: 'left', editable: 'never' },
            { title: <div style={{ lineHeight: 1.2 }}>Markup<br/>%</div>, field: 'pricingDetails.markup', type: 'numeric', align: 'left', editComponent: p => renderInput(p, 'markup', 25) },
            { title: <div style={{ lineHeight: 1.2 }}>Final<br/>Offer</div>, field: 'pricingDetails.finalOfferPrice', type: 'numeric', align: 'left', editable: 'never' },
            { title: <div style={{ lineHeight: 1.2 }}>Discount<br/>Price</div>, field: 'pricingDetails.discountedPrice', type: 'numeric', align: 'left', editable: 'never' },
            { 
                title: <div style={{ lineHeight: 1.2 }}>Markup<br/>Gain %</div>, 
                field: 'pricingDetails.actualMargin', 
                type: 'numeric', 
                align: 'left', 
                editable: 'never',
                render: (rowData) => {
                    const am = rowData.pricingDetails?.actualMargin;
                    if (am == null || isNaN(am)) return '0.00%';
                    return (am * 100).toFixed(2) + '%';
                }
            }
        ];

        if (enquiry.offerDetails?.offerType === 'Local Manufacturing') {
            columns.unshift({
                title: 'Include',
                field: 'localOffer.isSelected',
                type: 'boolean',
                render: row => (
                    <Checkbox 
                        checked={row.localOffer?.isSelected !== false} 
                        icon={<CheckCircleSharpIcon sx={{ color: '#ccc', fontSize: 20 }} />} 
                        checkedIcon={<CheckCircleSharpIcon color="success" sx={{ fontSize: 20 }} />}
                        onChange={(e) => {
                            const newChecked = e.target.checked;
                            setEnquiry(prev => {
                                const updated = [...prev.items];
                                const index = updated.findIndex(i => (i.tableData?.id === row.tableData?.id) || (i === row));
                                if (index > -1) {
                                    updated[index] = { 
                                        ...updated[index], 
                                        localOffer: { ...updated[index].localOffer, isSelected: newChecked } 
                                    };
                                }
                                return { ...prev, items: updated };
                            });
                        }}
                    />
                ),
                editComponent: p => <Checkbox checked={p.value !== false} onChange={(e) => p.onChange(e.target.checked)} icon={<CheckCircleSharpIcon sx={{ color: '#ccc', fontSize: 20 }} />} checkedIcon={<CheckCircleSharpIcon color="success" sx={{ fontSize: 20 }} />} />
            });
            // columns.push(
            //     {
            //         title: 'Size',
            //         field: 'localOffer.size',
            //         editComponent: (p) => (
            //             <input type="text" className="form-control-input bom-input" value={p.value ?? ''} onChange={(e) => p.onChange(e.target.value)} />
            //         )
            //     },
            //     {
            //         title: <div style={{ lineHeight: 1.2 }}>Calc<br/>Price</div>,
            //         field: 'localOffer.calculatedPrice',
            //         type: 'numeric',
            //         align: 'left',
            //         editComponent: (p) => (
            //             <input type="number" className="form-control-input bom-input no-spin" style={{ width: '100%', minWidth: '60px', textAlign: 'left', padding: '4px 8px' }} onWheel={(e) => e.target.blur()} value={p.value ?? 0} onChange={(e) => p.onChange(Number(e.target.value))} />
            //         )
            //     }
            // );
        } else if (enquiry.offerDetails?.offerType === 'Imported') {
            columns.unshift({
                title: 'Include',
                field: 'importedOffer.isSelected',
                type: 'boolean',
                render: row => (
                    <Checkbox 
                        checked={row.importedOffer?.isSelected !== false} 
                        icon={<CheckCircleSharpIcon sx={{ color: '#ccc', fontSize: 20 }} />} 
                        checkedIcon={<CheckCircleSharpIcon color="success" sx={{ fontSize: 20 }} />}
                        onChange={(e) => {
                            const newChecked = e.target.checked;
                            setEnquiry(prev => {
                                const updated = [...prev.items];
                                const index = updated.findIndex(i => (i.tableData?.id === row.tableData?.id) || (i === row));
                                if (index > -1) {
                                    updated[index] = { 
                                        ...updated[index], 
                                        importedOffer: { ...updated[index].importedOffer, isSelected: newChecked } 
                                    };
                                }
                                return { ...prev, items: updated };
                            });
                        }}
                    />
                ),
                editComponent: p => <Checkbox checked={p.value !== false} onChange={(e) => p.onChange(e.target.checked)} icon={<CheckCircleSharpIcon sx={{ color: '#ccc', fontSize: 20 }} />} checkedIcon={<CheckCircleSharpIcon color="success" sx={{ fontSize: 20 }} />} />
            });
            columns.push(
                {
                    title: 'Pos',
                    field: 'importedOffer.pos',
                    editComponent: (p) => (
                        <input type="text" className="form-control-input bom-input" value={p.value ?? ''} onChange={(e) => p.onChange(e.target.value)} />
                    )
                },
                {
                    title: <div style={{ lineHeight: 1.2 }}>Mat<br/>Desc</div>,
                    field: 'importedOffer.materialDescription',
                    editComponent: (p) => (
                        <input type="text" className="form-control-input bom-input" value={p.value ?? ''} onChange={(e) => p.onChange(e.target.value)} />
                    )
                },
                {
                    title: <div style={{ lineHeight: 1.2 }}>Price/<br/>Unit</div>,
                    field: 'importedOffer.pricePerUnit',
                    type: 'numeric',
                    align: 'left',
                    editComponent: (p) => (
                        <input type="number" className="form-control-input bom-input no-spin" style={{ width: '100%', minWidth: '60px', textAlign: 'left', padding: '4px 8px' }} onWheel={(e) => e.target.blur()} value={p.value ?? 0} onChange={(e) => p.onChange(Number(e.target.value))} />
                    )
                },
                {
                    title: <div style={{ lineHeight: 1.2 }}>Net<br/>Value</div>,
                    field: 'importedOffer.netValue',
                    type: 'numeric',
                    align: 'left',
                    editable: 'never',
                    render: (row) => (Number(row.quantity || 1) * Number(row.importedOffer?.pricePerUnit || 0)).toFixed(2)
                },
                {
                    title: 'Tariff',
                    field: 'importedOffer.customTariff',
                    editComponent: (p) => (
                        <input type="text" className="form-control-input bom-input" value={p.value ?? ''} onChange={(e) => p.onChange(e.target.value)} />
                    )
                }
            );
        }
    } 
        const renderAttachmentDropBox = ({
            category,
            label,
            buttonText,
            draftFiles,
            savedFiles
        }) => (
            <div className="col-md-3 mb-3">
                <label className="form-label d-block bom-label">{label}</label>
                <div
                    onDrop={(e) => handleFileDrop(category, e)}
                    onDragOver={handleDragOver}
                    style={{
                        border: '1px dashed #b8c2cc',
                        borderRadius: '8px',
                        padding: '12px',
                        background: '#fafbfc',
                        minHeight: '120px',
                        cursor: 'pointer',
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center',
                        justifyContent: 'center',
                        textAlign: 'center'
                    }}
                >
                    <Typography
                        variant="caption"
                        color="textSecondary"
                        sx={{ display: 'block', mt: 1 }}
                    >
                        Drag and drop files here
                    </Typography>
                    <Button
                        component="label"
                        startIcon={<CloudUploadIcon />}
                        size="small"
                        sx={{ textTransform: 'none', color: '#2d3d42' }}
                    >
                        {buttonText}
                        <input
                            type="file"
                            hidden
                            multiple
                            onChange={(e) => handleFileSelect(category, e)}
                        />
                    </Button>
                    {draftFiles.length > 0 && (
                        <Box sx={{ mt: 1, display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                            {draftFiles.map((f, i) => (
                                <Chip
                                    key={`draft-${category}-${i}`}
                                    label={f.name}
                                    size="small"
                                    color="primary"
                                    variant="outlined"
                                    onDelete={() => removeDraftFile(category, i)}
                                    deleteIcon={<DeleteForeverIcon />}
                                />
                            ))}
                        </Box>
                    )}

                    {savedFiles?.length > 0 && (
                        <Box sx={{ mt: 1 }}>
                            <Typography variant="caption" color="textSecondary">
                                Saved:
                            </Typography>

                            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mt: 0.5 }}>
                                {savedFiles.map((att, i) => (
                                    <Chip
                                        key={`exist-${category}-${i}`}
                                        label={att.file_name}
                                        size="small"
                                        variant="outlined"
                                        icon={<AttachFileIcon />}
                                        onClick={() => window.open(att.file_url, '_blank')}
                                        onDelete={() => removeExistingFile(category, i)}
                                        deleteIcon={<DeleteForeverIcon />}
                                        clickable
                                    />
                                ))}
                            </Box>
                        </Box>
                    )}
                </div>
            </div>
        );

    return (
        <div className="main-content bom-page">
            <div className="pagetitle row" style={{ color: 'black', marginTop: "-25px" }}>
                <div className="col-md-10 sm_device">
                    <FormatListNumberedIcon aria-hidden="true" />
                    <span style={{ marginLeft: '7px' }}>
                        {isEditMode ? (
                            <>
                                Update Enquiry - 
                                <span style={{ fontSize: '0.85em', opacity: 0.6, marginLeft: '5px', fontWeight: 'lighter' }}>
                                    {enquiry.enquiryNumber || '(Number pending Verification)'}
                                </span>
                            </>
                        ) : (
                            'Create New Enquiry'
                        )}
                    </span>
                </div>
                <div className="col-md-2 sm_device" style={{ textAlign: 'right' }}>
                </div>
            </div>
            <div className="bom-divider" />

            <div className="row" style={{ position: 'relative' }}> 
                <div className="col-md-12" style={{ display: 'flex', flexDirection: 'column' }}>
                    
                    <div className="bom-layout-card" style={{ flex: 1 }}>
                        <div className="bom-sidebar-header">
                            <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                <FormatListNumberedIcon sx={{ mr: 1, color: '#2d3d42', fontSize: 16 }} />
                                <span className="bom-sidebar-title">Customer & Basic Details</span>
                            </Box>
                        </div>
                        
                        <div className="row">
                            {renderFieldRow({
                                label: 'Customer',
                                required: true,
                                colSpace: 'col-md-6',
                                children: (
                                    <Select
                                        options={[
                                            ...customers.map(c => ({ ...c, value: c._id, label: c.customer_name })),
                                            { value: 'ADD_TEMP', label: '+ Add from Temporary Customer', color: '#ff8c00' }
                                        ]}
                                        value={enquiry.customer}
                                        onChange={(val) => handleSelectChange('customer', val)}
                                        placeholder="Select customer..."
                                        isClearable
                                        menuPortalTarget={document.body}
                                        styles={{
                                            ...selectStyles,
                                            option: (base, state) => ({
                                                ...base,
                                                ...selectStyles.option(base, state),
                                                color: state.data.value === 'ADD_TEMP' ? '#ff8c00' : base.color,
                                                fontWeight: state.data.value === 'ADD_TEMP' ? 'bold' : base.fontWeight,
                                            })
                                        }}
                                    />
                                )
                            })}
                            {renderFieldRow({
                                label: 'Region',
                                required: true,
                                colSpace: 'col-md-6',
                                children: (
                                    <Select
                                        options={regions.map(r => ({ value: r._id, label: r.name }))}
                                        value={enquiry.region}
                                        onChange={(val) => handleSelectChange('region', val)}
                                        placeholder="Select region..."
                                        isClearable
                                        menuPortalTarget={document.body}
                                        styles={selectStyles}
                                    />
                                )
                            })}
                         
                        </div>
                        
                        <div className="row">
                            {renderFieldRow({
                                label: 'Offer Requirement', required: true, colSpace: 'col-md-6',
                                children: (
                                    <Select
                                        options={[ { value: 'Detailed', label: 'Detailed' }, { value: 'Budgetary', label: 'Budgetary' } ]}
                                        value={enquiry.offerRequirement}
                                        onChange={(val) => handleSelectChange('offerRequirement', val)}
                                        menuPortalTarget={document.body} styles={selectStyles}
                                    />
                                )
                            })}
                            {renderFieldRow({
                                label: 'Expected date for Offer submission', colSpace: 'col-md-6',
                                children: (
                                    <input
                                        type="date"
                                        className="form-control-customer bom-input"
                                        name="targetDate"
                                        min={getMinTargetDate()} // Enforces the 1-week rule
                                        value={enquiry.targetDate}
                                        onChange={handleChangeText}
                                    />
                                )
                            })}
                        </div>
                        
                        <div className="bom-divider" style={{ margin: '20px 0' }} />

                        <div className="bom-sidebar-header mt-4">
                            <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                <FormatListNumberedIcon sx={{ mr: 1, color: '#2d3d42', fontSize: 16 }} />
                                <span className="bom-sidebar-title">Technical and Workpiece Specifications</span>
                            </Box>
                        </div>
                        <div className="validation-sub-header">
                            <span className="validation-sub-title">Machine Specification</span>
                        </div>
                        <div className="row">
                            {renderFieldRow({
                                label: 'Machine Manufacturer', required: true, colSpace: 'col-md-4',
                                children: (
                                    <Select
                                        options={[
                                            { value: 'ADD_NEW', label: '+ Add New Machine', color: '#ff8c00' },
                                            // Map your existing machine array here once connected to API
                                        ]}
                                        value={enquiry.machineManufacturer}
                                        onChange={(val) => {
                                            if(val?.value === 'ADD_NEW') setMachineDialogOpen(true);
                                            else handleSelectChange('machineManufacturer', val);
                                        }}
                                        placeholder="Select machine..."
                                        menuPortalTarget={document.body} styles={selectStyles}
                                    />
                                )
                            })}
                            {renderFieldRow({
                                label: 'Machine Model', required: true, colSpace: 'col-md-4',
                                children: (
                                    <input type="text" className="form-control-customer bom-input" name="machineModel" value={enquiry.machineModel} onChange={handleChangeText} />
                                )
                            })}
                            {renderFieldRow({
                                label: 'Machine Sr. no', colSpace: 'col-md-4',
                                children: (
                                    <input type="text" className="form-control-customer bom-input" name="machineSrNo" value={enquiry.machineSrNo} onChange={handleChangeText} />
                                )
                            })}
                        </div>

                        <div className="validation-sub-header mt-3">
                            <span className="validation-sub-title">Workpiece Details</span>
                        </div>
                        <div className="row">
                            {renderFieldRow({
                                label: 'Component Drawing Number(s)', required: true, colSpace: 'col-md-4',
                                children: (
                                    <CreatableSelect
                                        isMulti
                                        placeholder="Type and press Enter..."
                                        value={enquiry.componentDrawingNumbers}
                                        onChange={(val) => handleSelectChange('componentDrawingNumbers', val || [])}
                                        menuPortalTarget={document.body}
                                          styles={{
                                            ...selectStyles,
                                            control: (base) => ({
                                                ...base,
                                                minHeight: '90px',   
                                                height: 'auto',      
                                                alignItems: 'flex-start'
                                            }),
                                            placeholder: (base) =>({
                                                ...base,
                                                fontSize: '12px',
                                            }),
                                            input: (base) =>({
                                                ...base,
                                                fontSize: '12px',
                                            }),
                                            multiValue: (base) => ({
                                                ...base,
                                                fontSize: '12px',
                                            }),
                                        }}
                                />
                                )
                            })}
                            {renderFieldRow({
                                label: 'Component Volume / month', colSpace: 'col-md-4',
                                children: <input type="number" className="form-control-customer bom-input" name="componentVolumePerMonth" value={enquiry.componentVolumePerMonth} onChange={handleChangeText} />
                            })}
                            {renderFieldRow({
                                label: 'Component Stage', required: true, colSpace: 'col-md-4',
                                children: (
                                    <Select
                                        options={[ { value: 'NPD', label: 'NPD' }, { value: 'Production', label: 'Production' } ]}
                                        value={enquiry.componentStage}
                                        onChange={(val) => handleSelectChange('componentStage', val)}
                                        menuPortalTarget={document.body} styles={selectStyles} isClearable
                                    />
                                )
                            })}
                        </div>
                        
                        <div className="validation-sub-header mt-3">
                            <span className="validation-sub-title">Technical Specifications</span>
                        </div>

                        <div className="row mb-2">
                            <div className="col-md-4 mb-3 d-flex flex-column">
                                <label className="form-label d-block bom-label" style={{ marginBottom: '6px' }}>Fixture Scope<span className="text-danger ms-1">*</span></label>
                                <div className="d-flex align-items-center gap-4 mt-1" style={{ flex: 1 }}>
                                    <label className="d-flex align-items-center mb-0 bom-label" style={{ cursor: 'pointer' }}>
                                        <Checkbox 
                                            checked={enquiry.fullFixture} 
                                            onChange={(e) => setEnquiry(prev => ({ ...prev, fullFixture: e.target.checked }))} 
                                            sx={{ padding: '0 6px 0 0', '& .MuiSvgIcon-root': { fontSize: 16 } }} 
                                        />
                                        Full Fixture</label>
                                    <label className="d-flex align-items-center mb-0 bom-label" style={{ cursor: 'pointer' }}>
                                        <Checkbox 
                                            checked={enquiry.changeParts} 
                                            onChange={(e) => setEnquiry(prev => ({ ...prev, changeParts: e.target.checked }))} 
                                            sx={{ padding: '0 6px 0 0', '& .MuiSvgIcon-root': { fontSize: 16 } }} 
                                        />
                                        Change Parts</label>
                                </div>
                            </div>
                            
                            {renderFieldRow({
                                label: 'Expected Fixture Concept', colSpace: 'col-md-4',
                                children: (
                                    <Select
                                        options={[
                                            { value: 'Cylindrical - Collet Type', label: 'Cylindrical - Collet Type' },
                                            { value: 'Cylindrical - Friction Type', label: 'Cylindrical - Friction Type' },
                                            { value: 'Cylindrical – Special', label: 'Cylindrical – Special' },
                                            { value: 'Bevel – Pinion', label: 'Bevel – Pinion' },
                                            { value: 'Bevel – Crownwheel', label: 'Bevel – Crownwheel' },
                                            { value: 'Special need', label: 'Special need' }
                                        ]}
                                        value={enquiry.expectedFixtureConcept}
                                        onChange={(val) => handleSelectChange('expectedFixtureConcept', val)}
                                        menuPortalTarget={document.body} styles={selectStyles} isClearable
                                    />
                                )
                            })}

                            {/* Conditionally render remark box */}
                            {enquiry.expectedFixtureConcept?.value === 'Special need' && (
                                renderFieldRow({
                                    label: 'Special Need Remark', colSpace: 'col-md-4',
                                    children: <input type="text" className="form-control-customer bom-input" name="specialNeedRemark" value={enquiry.specialNeedRemark} onChange={handleChangeText} />
                                })
                            )}
                        </div>

                        <div className="row">
                            {renderFieldRow({
                                label: 'Component Manufacturing Process', required: true, colSpace: 'col-md-4',
                                children: <input type="text" className="form-control-customer bom-input" name="componentManufacturingProcess" value={enquiry.componentManufacturingProcess} onChange={handleChangeText} />
                            })}
                            {renderFieldRow({
                                label: 'Cutting Tool Max Diameter', colSpace: 'col-md-4',
                                children: <input type="number" className="form-control-customer bom-input" name="cuttingToolMaxDiameter" value={enquiry.cuttingToolMaxDiameter} onChange={handleChangeText} />
                            })}
                            {renderFieldRow({
                                label: 'Cutting Tool Min Diameter', colSpace: 'col-md-4',
                                children: <input type="number" className="form-control-customer bom-input" name="cuttingToolMinDiameter" value={enquiry.cuttingToolMinDiameter} onChange={handleChangeText} />
                            })}
                        </div>
                        <div className="row">
                            {renderFieldRow({
                                label: 'Hydraulic Leadthrough',
                                required: true,
                                colSpace: 'col-md-4',
                                children: (
                                    <Select
                                        options={[
                                            { value: 'Single Port', label: 'Single Port' },
                                            { value: 'Multiport', label: 'Multiport' },
                                            { value: 'Drawbar', label: 'Drawbar' },
                                            { value: 'None', label: 'None' }
                                        ]}
                                        value={enquiry.hydraulicLeadthrough}
                                        onChange={(val) => handleSelectChange('hydraulicLeadthrough', val)}
                                        menuPortalTarget={document.body}
                                        styles={selectStyles}
                                    />
                                )
                            })}
                            {renderFieldRow({
                                label: 'Fixture Centering',
                                required: true,
                                colSpace: 'col-md-4',
                                children: (
                                    <Select
                                        options={[
                                            { value: 'Self centering', label: 'Self centering' },
                                            { value: 'Manual truing', label: 'Manual truing' }
                                        ]}
                                        value={enquiry.fixtureCentering}
                                        onChange={(val) => handleSelectChange('fixtureCentering', val)}
                                        menuPortalTarget={document.body}
                                        styles={selectStyles}
                                    />
                                )
                            })}
                            {renderFieldRow({
                                label: 'Table Adaptations / Base chucks / Existing Fixture',
                                colSpace: 'col-md-4',
                                children: (
                                   /* <input type="text" className="form-control-customer bom-input" name="tableAdaptations" value={enquiry.tableAdaptations} onChange={handleChangeText} />*/
                                   <textarea 
                                   className="form-control-customer bom-input table-adaptation-textarea"
                                   name="tableAdaptations"
                                   rows={5}
                                   placeholder="Enter table adaptations..."
                                   value={enquiry.tableAdaptations} 
                                   onChange={handleChangeText}
                                   />
                                )
                            })}
                        </div>

                        <div className="validation-sub-header mt-3">
                            <span className="validation-sub-title">Fixture for Machine Acceptance</span>
                        </div>
                        <div className="row mb-3" style={{ paddingLeft: '15px' }}>
                            <div className="col-md-12 d-flex flex-wrap gap-4 mt-2">
                                <label className="d-flex align-items-center mb-0 bom-label" style={{ cursor: 'pointer' }}>
                                    <Checkbox checked={enquiry.machineAcceptancePAC} onChange={(e) => handleChangeText({ target: { name: 'machineAcceptancePAC', type: 'checkbox', checked: e.target.checked } })} sx={{ padding: '0 6px 0 0', '& .MuiSvgIcon-root': { fontSize: 16 } }} />
                                    PAC <span className="text-danger ms-1">*</span>
                                </label>
                                <label className="d-flex align-items-center mb-0 bom-label" style={{ cursor: 'pointer' }}>
                                    <Checkbox checked={enquiry.machineAcceptanceFAC} onChange={(e) => handleChangeText({ target: { name: 'machineAcceptanceFAC', type: 'checkbox', checked: e.target.checked } })} sx={{ padding: '0 6px 0 0', '& .MuiSvgIcon-root': { fontSize: 16 } }} />
                                    FAC <span className="text-danger ms-1">*</span>
                                </label>
                                <label className="d-flex align-items-center mb-0 bom-label" style={{ cursor: 'pointer' }}>
                                    <Checkbox checked={enquiry.machineAcceptanceNone} onChange={(e) => handleChangeText({ target: { name: 'machineAcceptanceNone', type: 'checkbox', checked: e.target.checked } })} sx={{ padding: '0 6px 0 0', '& .MuiSvgIcon-root': { fontSize: 16 } }} />
                                    None
                                </label>
                            </div>
                        </div>

                        <div className="validation-sub-header mt-3">
                            <span className="validation-sub-title">Additional Comments</span>
                        </div>
                        <div className="row">
                            <div className="col-md-12 mb-3">
                                <textarea
                                    className="form-control-customer bom-input"
                                    name="additionalComments"
                                    rows="4"
                                    placeholder="Any other comments or remarks..."
                                    value={enquiry.additionalComments}
                                    onChange={handleChangeText}
                                />
                            </div>
                        </div>

                       {/* <div className="validation-sub-header mt-3">
                            <span className="validation-sub-title">Additional Requirements</span>
                        </div>
                        <div className="row mb-3" style={{ paddingLeft: '15px' }}>
                            <div className="col-md-12 d-flex flex-wrap gap-4 mt-2">
                                <label className="d-flex align-items-center mb-0 bom-label" style={{ cursor: 'pointer', minWidth: '180px' }}>
                                    <Checkbox checked={enquiry.commonisationRequired} onChange={(e) => handleChangeText({ target: { name: 'commonisationRequired', type: 'checkbox', checked: e.target.checked } })} sx={{ padding: '0 6px 0 0', '& .MuiSvgIcon-root': { fontSize: 16 } }} />
                                    Commonisation Required <span className="text-danger ms-1">*</span>
                                </label>
                                <label className="d-flex align-items-center mb-0 bom-label" style={{ cursor: 'pointer', minWidth: '180px' }}>
                                    <Checkbox checked={enquiry.airSensingRequired} onChange={(e) => handleChangeText({ target: { name: 'airSensingRequired', type: 'checkbox', checked: e.target.checked } })} sx={{ padding: '0 6px 0 0', '& .MuiSvgIcon-root': { fontSize: 16 } }} />
                                    Air Sensing Required <span className="text-danger ms-1">*</span>
                                </label>
                                <label className="d-flex align-items-center mb-0 bom-label" style={{ cursor: 'pointer', minWidth: '180px' }}>
                                    <Checkbox checked={enquiry.chipFlushingRequired} onChange={(e) => handleChangeText({ target: { name: 'chipFlushingRequired', type: 'checkbox', checked: e.target.checked } })} sx={{ padding: '0 6px 0 0', '& .MuiSvgIcon-root': { fontSize: 16 } }} />
                                    Chip Flushing Required <span className="text-danger ms-1">*</span>
                                </label>
                                <label className="d-flex align-items-center mb-0 bom-label" style={{ cursor: 'pointer', minWidth: '180px' }}>
                                    <Checkbox checked={enquiry.topAssemblyRequired} onChange={(e) => handleChangeText({ target: { name: 'topAssemblyRequired', type: 'checkbox', checked: e.target.checked } })} sx={{ padding: '0 6px 0 0', '& .MuiSvgIcon-root': { fontSize: 16 } }} />
                                    Top Assembly Required <span className="text-danger ms-1">*</span>
                                </label>
                                <label className="d-flex align-items-center mb-0 bom-label" style={{ cursor: 'pointer', minWidth: '180px' }}>
                                    <Checkbox checked={enquiry.productionStudyAvailable} onChange={(e) => handleChangeText({ target: { name: 'productionStudyAvailable', type: 'checkbox', checked: e.target.checked } })} sx={{ padding: '0 6px 0 0', '& .MuiSvgIcon-root': { fontSize: 16 } }} />
                                    Production Study Available <span className="text-danger ms-1">*</span>
                                </label>
                                <label className="d-flex align-items-center mb-0 bom-label" style={{ cursor: 'pointer', minWidth: '180px' }}>
                                    <Checkbox checked={enquiry.processDrawingsAvailable} onChange={(e) => handleChangeText({ target: { name: 'processDrawingsAvailable', type: 'checkbox', checked: e.target.checked } })} sx={{ padding: '0 6px 0 0', '& .MuiSvgIcon-root': { fontSize: 16 } }} />
                                    Process drawings <span className="text-danger ms-1">*</span>
                                </label>
                            </div>
                        </div>*/}
                        
                        <div className="validation-sub-header mt-3">
                            <span className="validation-sub-title">Additional Requirements</span>
                        </div>

                        <div className="row mb-3" style={{ paddingLeft: '15px' }}>
                            <div className="col-md-12 d-flex flex-wrap gap-4 mt-2">

                                <div className="d-flex align-items-center">
                                    <span className="bom-label me-2">Commonisation Required</span>
                                    <Switch
                                        checked={enquiry.commonisationRequired}
                                        onChange={(e) =>
                                            handleChangeText({
                                                target: {
                                                    name: 'commonisationRequired',
                                                    type: 'checkbox',
                                                    checked: e.target.checked
                                                }
                                            })
                                        }
                                    />
                                </div>

                                <div className="d-flex align-items-center">
                                    <span className="bom-label me-2">Air Sensing Required</span>
                                    <Switch
                                        checked={enquiry.airSensingRequired}
                                        onChange={(e) =>
                                            handleChangeText({
                                                target: {
                                                    name: 'airSensingRequired',
                                                    type: 'checkbox',
                                                    checked: e.target.checked
                                                }
                                            })
                                        }
                                    />
                                </div>

                                <div className="d-flex align-items-center">
                                    <span className="bom-label me-2">Chip Flushing Required</span>
                                    <Switch
                                        checked={enquiry.chipFlushingRequired}
                                        onChange={(e) =>
                                            handleChangeText({
                                                target: {
                                                    name: 'chipFlushingRequired',
                                                    type: 'checkbox',
                                                    checked: e.target.checked
                                                }
                                            })
                                        }
                                    />
                                </div>

                                <div className="d-flex align-items-center">
                                    <span className="bom-label me-2">Top Assembly Required</span>
                                    <Switch
                                        checked={enquiry.topAssemblyRequired}
                                        onChange={(e) =>
                                            handleChangeText({
                                                target: {
                                                    name: 'topAssemblyRequired',
                                                    type: 'checkbox',
                                                    checked: e.target.checked
                                                }
                                            })
                                        }
                                    />
                                </div>

                                <div className="d-flex align-items-center">
                                    <span className="bom-label me-2">Production Study Available</span>
                                    <Switch
                                        checked={enquiry.productionStudyAvailable}
                                        onChange={(e) =>
                                            handleChangeText({
                                                target: {
                                                    name: 'productionStudyAvailable',
                                                    type: 'checkbox',
                                                    checked: e.target.checked
                                                }
                                            })
                                        }
                                    />
                                </div>

                                <div className="d-flex align-items-center">
                                    <span className="bom-label me-2">Process Drawings Available</span>
                                    <Switch
                                        checked={enquiry.processDrawingsAvailable}
                                        onChange={(e) =>
                                            handleChangeText({
                                                target: {
                                                    name: 'processDrawingsAvailable',
                                                    type: 'checkbox',
                                                    checked: e.target.checked
                                                }
                                            })
                                        }
                                    />
                                </div>
                             </div>
                        </div>
                        <div className="bom-divider" style={{ margin: '20px 0' }} />

                        <div className="validation-sub-header mt-3">
                            <span className="validation-sub-title">Attachments</span>
                        </div>

                        {/* Upload progress bar */}
                        {isUploading && (
                            <Box sx={{ mb: 2, px: 1 }}>
                                <LinearProgress variant="determinate" value={uploadPct} sx={{ height: 6, borderRadius: 3 }} />
                                <Typography variant="caption" color="textSecondary" sx={{ mt: 0.5 }}>{uploadPct}% Uploading...</Typography>
                            </Box>
                        )}

                        <div className="row">
                            {renderAttachmentDropBox({
                                category: 'partDrawings',
                                label: 'Component Drawings',
                                buttonText: 'Upload Drawings',
                                draftFiles: partDrawingsDraft,
                                savedFiles: enquiry.attachments?.partDrawings || []
                            })}

                            {renderAttachmentDropBox({
                                category: 'fixtureSampleLayouts',
                                label: 'Fixture Layouts',
                                buttonText: 'Upload Layouts',
                                draftFiles: fixtureSampleLayoutsDraft,
                                savedFiles: enquiry.attachments?.fixtureSampleLayouts || []
                            })}

                            {renderAttachmentDropBox({
                                category: 'customerEnquiryEmail',
                                label: 'Enquiry Email',
                                buttonText: 'Upload Email',
                                draftFiles: customerEnquiryEmailDraft,
                                savedFiles: enquiry.attachments?.customerEnquiryEmail || []
                            })}

                            {renderAttachmentDropBox({
                                category: 'clampingConceptCutouts',
                                label: 'Clamping Concepts',
                                buttonText: 'Upload Concepts',
                                draftFiles: clampingConceptCutoutsDraft,
                                savedFiles: enquiry.attachments?.clampingConceptCutouts || []
                            })}
                        </div>

                            {isEditMode && enquiry.status !== 'Enquiry Generated' && (
                            <>
                                <div className="bom-divider" style={{ margin: '20px 0' }} />
                                <div className="bom-sidebar-header mt-4">
                                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                        <FormatListNumberedIcon sx={{ mr: 1, color: '#2d3d42', fontSize: 16 }} />
                                        <span className="bom-sidebar-title">Verification</span>
                                    </Box>
                                </div>
                                <div className="row mt-3">
                                    <div className="col-md-12">
                                        <div className="card bom-card p-3">
                                            <div className="row">
                                                <div className="col-md-6 mb-3">
                                                    <label className="form-label bom-label d-block">Business Vertical <span className="text-danger">*</span></label>
                                                    <Select
                                                        options={businessVerticals.map(v => ({ value: v._id, label: v.name }))}
                                                        value={enquiry.businessVertical}
                                                        onChange={(opt) => handleSelectChange('businessVertical', opt)}
                                                        placeholder="Select Business Vertical"
                                                        isClearable
                                                        isDisabled={enquiry.status !== 'Verification'}
                                                        menuPortalTarget={document.body} 
                                                        styles={selectStyles}
                                                    />
                                                </div>
                                                <div className="col-md-6 mb-3">
                                                    <label className="form-label bom-label d-block">Forward for Feasibility - Design Engineer</label>
                                                    <Select
                                                        options={designEngineers.map(u => ({ value: u._id, label: `${u.first_name} ${u.last_name}` }))}
                                                        value={enquiry.designEngineerId}
                                                        onChange={(opt) => handleSelectChange('designEngineerId', opt)}
                                                        placeholder="Select Design Engineer"
                                                        isClearable
                                                        isDisabled={enquiry.status !== 'Verification'}
                                                        menuPortalTarget={document.body} 
                                                        styles={selectStyles}
                                                    />
                                                </div>
                                                <div className="col-md-12 mb-3">
                                                    <label className="form-label bom-label d-block">Verification Comment</label>
                                                    <TextField
                                                        fullWidth
                                                        size="small"
                                                        multiline
                                                        rows={2}
                                                        name="verificationComment"
                                                        value={enquiry.verificationComment || ''}
                                                        onChange={handleChangeText}
                                                        placeholder="Enter comment..."
                                                        disabled={enquiry.status !== 'Verification'}
                                                    />
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </>
                        )}

                        <div className="bom-divider" style={{ margin: '20px 0' }} />

                        {(!isEditMode || !enquiry.status || enquiry.status === 'Enquiry Generated') ? null : (
                            <>
                                <div className="bom-table-header" style={{ backgroundColor: '#f5f5f5', marginTop: '20px' }}>
                                    <span className="bom-table-title">Parts & Feasibility Studies</span>
                                    <Button
                                        className="bom-add-btn"
                                        variant="contained"
                                        startIcon={<AddBoxIcon className="bom-add-icon" />}
                                        onClick={handleOpenAddPartDialog}
                                        sx={{
                                            backgroundColor: '#2d3d42',
                                            '&:hover': {
                                                backgroundColor: '#008BCF'
                                            }
                                        }}
                                    >
                                        Add Part
                                    </Button>
                                </div>
                        <ThemeProvider theme={theme}>
                            <Paper elevation={0} style={{ border: '1px solid #e0e0e0', borderRadius: '4px' }}>
                                <MaterialTable
                                    title={null}
                                    columns={columns}
                                    data={enquiry.items}
                                    style={{ boxShadow: 'none', border: 'none' }}
                                editable={{
                                    onRowUpdate: async (newData, oldData) => {
                                        setEnquiry(prev => {
                                            const updated = [...prev.items];
                                            const index = updated.findIndex((i) => i.tableData?.id === oldData.tableData?.id);
                                            if (index > -1) {
                                                const mergedItem = { ...updated[index], ...newData, feasibilityStudy: oldData.feasibilityStudy || { ...emptyStudySchema } };
                                                if (mergedItem.importedOffer) {
                                                    mergedItem.importedOffer.netValue = Number(mergedItem.quantity || 1) * Number(mergedItem.importedOffer.pricePerUnit || 0);
                                                }
                                                updated[index] = mergedItem;
                                            }
                                            return { ...prev, items: updated };
                                        });
                                    },
                                    onRowDelete: async (oldData) => {
                                        setEnquiry(prev => ({
                                            ...prev,
                                            items: prev.items.filter((i) => i.tableData?.id !== oldData.tableData?.id)
                                        }));
                                    }
                                }}
                                detailPanel={(rowData) => {
                                    const index = enquiry.items.findIndex(i => i.tableData?.id === rowData.tableData?.id);
                                    if(index === -1) return null;
                                    return (
                                        <div style={{ padding: '20px', background: '#eef2f5', minHeight: '500px', boxSizing: 'border-box', overflowX: 'hidden' }}>
                                            <FeasibilityStudyCard 
                                                study={enquiry.items[index]?.feasibilityStudy}
                                                index={index}
                                                onChange={handleItemFeasibilityChange}
                                            />
                                        </div>
                                    );
                                }}
                                options={{
                                    actionsColumnIndex: -1,
                                    search: false,
                                    filtering: false,
                                    paging: false,
                                    detailPanelType: 'single',
                                    padding: 'dense', 
                                    headerStyle: {
                                        backgroundColor: '#fafbfc',
                                        color: '#2d3d42',
                                        fontWeight: 600,
                                        fontSize: '13px',
                                        whiteSpace: 'nowrap',
                                        textAlign: 'left',
                                        position: 'sticky',
                                        top: 0,
                                        zIndex: 10,
                                    },
                                    rowStyle: {
                                        fontSize: '13px'
                                    },
                                    maxBodyHeight: '500px',
                                    overflowY: 'auto',
                                    sorting:false,
                                }}
                            />
                            {enquiry.status !== 'Enquiry Generated' && enquiry.status !== 'Feasibility Study' && (
                                <div style={{ borderTop: '1px solid rgba(224, 224, 224, 1)' }}>
                                    <OfferDetailsSection 
                                        offerDetails={enquiry.offerDetails} 
                                        setEnquiry={setEnquiry}
                                        enquiry={enquiry}
                                        quotationMetadata={quotationMetadata}
                                    />
                                </div>
                            )}
                        </Paper>
                        </ThemeProvider>

                            </>
                        )}

                        <div className="row mt-4 mb-3">
                            <div className="col-md-12 d-flex">
                                {!isEditMode ? (
                                    <button
                                        type="button"
                                        className="release-to-manufacturing-button m-3"
                                        onClick={() => handleSave('Enquiry Generated')}
                                        disabled={isSaving || isUploading}
                                    >
                                        {isUploading ? 'Uploading files...' : isSaving ? 'Saving...' : 'Generate Enquiry'}
                                    </button>
                                ) : (
                                    <>
                                        <button
                                            type="button"
                                            className="release-to-manufacturing-button m-3"
                                            onClick={onApproveClick}
                                            disabled={isSaving || isUploading}
                                        >
                                            {isUploading ? 'Uploading files...' : isSaving ? 'Saving...' : 'Approve & Move Next'}
                                        </button>
                                        {enquiry.status === 'Offer Submitted' && (
                                            <button
                                                type="button"
                                                className="brand-button m-3"
                                                onClick={() => history.push(`/create-po/${routeEnquiryId}`)}
                                                disabled={isSaving || isUploading}
                                            >
                                                Convert to PO
                                            </button>
                                        )}
                                        <button
                                            type="button"
                                            className="brand-button-inverse m-3"
                                            onClick={() => handleSave()}
                                            disabled={isSaving || isUploading}
                                        >
                                            {isUploading ? 'Uploading files...' : isSaving ? 'Saving...' : 'Update Enquiry'}
                                        </button>
                                    </>
                                )}
                                <button
                                    type="button"
                                    className="brand-button-inverse m-3"
                                    onClick={() => history.push('/enquiry-list')}
                                    disabled={isSaving}
                                >
                                    Cancel
                                </button>
                            </div>
                        </div>

                    </div>
                </div>

                {/* ---- STAGE APPROVAL COMMENT DIALOG ---- */}
                <Dialog open={isCommentDialogOpen} onClose={() => setIsCommentDialogOpen(false)} maxWidth="sm" fullWidth sx={{ zIndex: 9999 }}>
                    <DialogTitle>Stage Approval Instructions</DialogTitle>
                    <DialogContent dividers>
                        <Typography variant="body1" color="text.secondary" gutterBottom>
                            You are moving this project to <strong>{pendingNextStage}</strong>. 
                            Please add any specific instructions or comments for this stage (Optional).
                        </Typography>
                        <TextField
                            autoFocus
                            label="Comments"
                            fullWidth
                            multiline
                            rows={4}
                            variant="outlined"
                            value={approvalComment}
                            onChange={(e) => setApprovalComment(e.target.value)}
                            placeholder="e.g., Verified DAP, proceed with procurement..."
                            InputLabelProps={{
                                shrink: true,
                                sx: { 
                                    bgcolor: 'background.paper', 
                                    padding: '0 8px',           
                                }
                            }}
                        />
                    </DialogContent>
                    <DialogActions>
                        <Button className="bom-dialog-btn" onClick={() => setIsCommentDialogOpen(false)} color="inherit">
                            Cancel
                        </Button>
                        <Box sx={{ flex: 1 }} />
                        
                        <Button 
                            className="bom-dialog-btn"
                            onClick={() => {
                                setIsCommentDialogOpen(false);
                                handleFinalApproval(""); 
                            }} 
                            color="primary"
                        >
                            Skip
                        </Button>

                        <Button 
                            className="bom-dialog-btn"
                            onClick={() => {
                                setIsCommentDialogOpen(false);
                                handleFinalApproval(approvalComment); 
                            }} 
                            variant="contained" 
                            color="primary"
                        >
                            Confirm & Approve
                        </Button>
                    </DialogActions>
                </Dialog>
            </div>

            {/* Add Part Dialog */}
            <Dialog open={addPartDialogOpen} onClose={handleCloseAddPartDialog} maxWidth="sm" fullWidth sx={{ zIndex: 9999 }}>
                <DialogTitle sx={{ pb: 1, borderBottom: '1px solid #eee', mb: 2 }}>Add New Part</DialogTitle>
                <DialogContent>
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2.5, mt: 1 }}>
                        <div>
                            <label className="form-label d-block bom-label mb-1">Enter Component Numbers <span className="text-danger">*</span></label>
                            <CreatableSelect
                                isMulti
                                options={componentOptions}
                                placeholder="Select Component Number..."
                                value={newPartData.component_drawing_no}
                                onChange={(val) =>
                                    setNewPartData(prev => ({
                                        ...prev,
                                        component_drawing_no: val || []
                                    }))
                                }
                                menuPortalTarget={document.body}
                                styles={{
                                    ...selectStyles,
                                    control: (base) => ({
                                        ...base,
                                        minHeight: '90px',
                                        height: 'auto',
                                        alignItems: 'flex-start'
                                    }),
                                    placeholder: (base) => ({
                                        ...base,
                                        fontSize: '12px',
                                    }),
                                    input: (base) => ({
                                        ...base,
                                        fontSize: '12px',
                                    }),
                                    multiValue: (base) => ({
                                        ...base,
                                        fontSize: '12px',
                                    }),
                                }}
                            />
                        </div>
                        <Grid container spacing={2}>
                            <Grid item xs={12} sm={4}>
                                <label className="form-label d-block bom-label mb-1">Clamping Dia</label>
                                <TextField
                                    fullWidth
                                    value={newPartData.clamping_dia}
                                    onChange={(e) => setNewPartData(prev => ({ ...prev, clamping_dia: e.target.value }))}
                                    size="small"
                                />
                            </Grid>
                            
                            <Grid item xs={12} sm={4}>
                                <label className="form-label d-block bom-label mb-1">Quantity</label>
                                <TextField
                                    type="number"
                                    fullWidth
                                    value={newPartData.quantity}
                                    onChange={(e) => setNewPartData(prev => ({ ...prev, quantity: Math.max(1, Number(e.target.value)) }))}
                                    size="small"
                                    InputProps={{ inputProps: { min: 1 } }}
                                />
                            </Grid>
                        </Grid>
                        <div>
                            <label className="form-label d-block bom-label mb-1">Component Type <span className="text-danger">*</span></label>
                            <Select
                                options={COMPONENT_TYPES}
                                value={newPartData.component_type}
                                onChange={(opt) => setNewPartData(prev => ({ ...prev, component_type: opt }))}
                                menuPortalTarget={document.body} 
                                styles={selectStyles}
                            />
                        </div>
                        <div>
                            <label className="form-label d-block bom-label mb-1">Fixture Concept</label>
                            <Select
                                options={FIXTURE_CONCEPTS}
                                value={newPartData.fixture_concept}
                                onChange={(opt) => setNewPartData(prev => {
                                    const updated = { ...prev, fixture_concept: opt };
                                    if (opt?.value !== 'Special' && prev.fixture_type?.value === 'Special') {
                                        updated.fixture_type = null;
                                    }
                                    return updated;
                                })}
                                menuPortalTarget={document.body} 
                                styles={selectStyles}
                            />
                        </div>
                        <div>
                            <label className="form-label d-block bom-label mb-1">Fixture Type <span className="text-danger">*</span></label>
                            <Select
                                options={getFilteredFixtureTypes()}
                                value={newPartData.fixture_type}
                                onChange={(opt) => setNewPartData(prev => ({ ...prev, fixture_type: opt }))}
                                menuPortalTarget={document.body} 
                                styles={selectStyles}
                            />
                        </div>
                    </Box>
                </DialogContent>
                <DialogActions sx={{ p: 2, borderTop: '1px solid #eee' }}>
                    <Button className='bom-btn-cancel' onClick={handleCloseAddPartDialog} color="inherit" sx={{ textTransform: 'none' }}>Cancel</Button>
                    <Button className='bom-btn-save' onClick={handleSaveNewPart} variant="contained" sx={{ textTransform: 'none' }}>Add Part</Button>
                </DialogActions>
            </Dialog>

            {/* Temporary Customer Dialog */}
            <Dialog open={tempCustDialogOpen} onClose={handleCloseTempCustDialog} maxWidth="md" fullWidth sx={{ zIndex: 9999 }}>
                <DialogTitle sx={{ pb: 1, borderBottom: '1px solid #eee' }}>Temporary Customer Management</DialogTitle>
                <DialogContent sx={{ mt: 2 }}>
                    <Tabs 
                        value={tempTabValue} 
                        onChange={(e, val) => setTempTabValue(val)} 
                        sx={{ mb: 3, borderBottom: 1, borderColor: 'divider' }}
                    >
                        <Tab 
                            label="Select Existing Temporary Customer" 
                            sx={{ textTransform: 'none', fontSize: '12px' }} 
                        />
                        <Tab 
                            label="Add New Temporary Customer" 
                            sx={{ textTransform: 'none', fontSize: '12px' }} 
                        />
                    </Tabs>

                    {tempTabValue === 0 && (
                        <Box sx={{ minHeight: '200px' }}>
                            <label className="form-label d-block bom-label mb-2">Select from past temporary customers</label>
                            <Select
                                options={tempCustomers.map(c => ({ value: c._id, label: c.customer_name, data: c }))}
                                onChange={(opt) => {
                                    if (opt) {
                                        setEnquiry(prev => ({ ...prev, customer: { value: opt.value, label: opt.label } }));
                                        handleCloseTempCustDialog();
                                    }
                                }}
                                placeholder="Search temporary customers..."
                                isClearable
                                menuPortalTarget={document.body}
                                styles={selectStyles}
                            />
                            <Box sx={{ mt: 3, textAlign: 'center' }}>
                                <Typography variant="body2" color="textSecondary" sx={{ fontSize: '10px' }}>
                                    Customer not found? 
                                    <Button size="small" sx={{ ml: 1, textTransform: 'none', fontSize: '10px' }} onClick={() => setTempTabValue(1)}>
                                        Add Temporary Customer
                                    </Button>
                                </Typography>
                            </Box>
                        </Box>
                    )}

                    {tempTabValue === 1 && (
                        <Box sx={{ mt: 1 }}>
                            <Grid container spacing={2}>
                                <Grid item xs={12} md={6}>
                                    <label className="form-label d-block bom-label mb-1">Customer Name <span className="text-danger">*</span></label>
                                    <TextField
                                        fullWidth
                                        size="small"
                                        value={newTempCust.customer_name}
                                        onChange={(e) => setNewTempCust(prev => ({ ...prev, customer_name: e.target.value }))}
                                    />
                                </Grid>
                                <Grid item xs={12} md={6}>
                                    <label className="form-label d-block bom-label mb-1">Email address <span className="text-danger">*</span></label>
                                    <TextField
                                        fullWidth
                                        size="small"
                                        type="email"
                                        value={newTempCust.email}
                                        onChange={(e) => setNewTempCust(prev => ({ ...prev, email: e.target.value }))}
                                    />
                                </Grid>
                                <Grid item xs={12}>
                                    <label className="form-label d-block bom-label mb-1">Address <span className="text-danger">*</span></label>
                                    <TextField
                                        fullWidth
                                        size="small"
                                        multiline
                                        rows={2}
                                        value={newTempCust.full_address}
                                        onChange={(e) => setNewTempCust(prev => ({ ...prev, full_address: e.target.value }))}
                                    />
                                </Grid>
                                <Grid item xs={12} md={4}>
                                    <label className="form-label d-block bom-label mb-1">Pincode</label>
                                    <TextField
                                        fullWidth
                                        size="small"
                                        value={newTempCust.pin_code}
                                        onChange={(e) => setNewTempCust(prev => ({ ...prev, pin_code: e.target.value }))}
                                    />
                                </Grid>
                                <Grid item xs={12} md={4}>
                                    <label className="form-label d-block bom-label mb-1">Contact Person <span className="text-danger">*</span></label>
                                    <TextField
                                        fullWidth
                                        size="small"
                                        value={newTempCust.contact_name}
                                        onChange={(e) => setNewTempCust(prev => ({ ...prev, contact_name: e.target.value }))}
                                    />
                                </Grid>
                                <Grid item xs={12} md={4}>
                                    <label className="form-label d-block bom-label mb-1">Designation</label>
                                    <TextField
                                        fullWidth
                                        size="small"
                                        value={newTempCust.designation}
                                        onChange={(e) => setNewTempCust(prev => ({ ...prev, designation: e.target.value }))}
                                    />
                                </Grid>
                                <Grid item xs={12} md={6}>
                                    <label className="form-label d-block bom-label mb-1">Phone Number</label>
                                    <TextField
                                        fullWidth
                                        size="small"
                                        value={newTempCust.phone_number}
                                        onChange={(e) => setNewTempCust(prev => ({ ...prev, phone_number: e.target.value }))}
                                    />
                                </Grid>
                            </Grid>
                        </Box>
                    )}
                </DialogContent>
                <DialogActions sx={{ p: 2, borderTop: '1px solid #eee' }}>
                    <Button className='bom-btn-cancel' onClick={handleCloseTempCustDialog} color="inherit" sx={{ textTransform: 'none' }}>Cancel</Button>
                    {tempTabValue === 1 && (
                        <Button 
                            onClick={handleSaveNewTempCust} 
                            className='bom-btn-save'
                            variant="contained" 
                            disabled={isSavingTempCust}
                        >
                            {isSavingTempCust ? 'Saving...' : 'Add Temporary Customer'}
                        </Button>
                    )}
                </DialogActions>
            </Dialog>

            {/* Add Machine Dialog */}
            <Dialog 
                sx={{ zIndex: 9999 }}
                className="custom_modal"    
                open={machineDialogOpen} 
                onClose={() => setMachineDialogOpen(false)}
                maxWidth="sm"
                fullWidth
            >
                <DialogTitle sx={{ bgcolor: '#f8f9fa', borderBottom: '1px solid #eee' }}>
                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                        <AddCircleIcon sx={{ mr: 1, color: '#1976d2' }} />
                        <Typography variant="h6" sx={{ fontWeight: 'bold', color: '#2d3d42' }}>Add New Machine</Typography>
                    </Box>
                </DialogTitle>
                <DialogContent sx={{ mt: 2 }}>
                    <Grid container spacing={2}>
                        <Grid item xs={12}>
                            <label className="form-label d-block bom-label mb-1">Machine Manufacturer <span className="text-danger">*</span></label>
                            <TextField
                                fullWidth
                                size="small"
                                placeholder="e.g. Mazak, Haas"
                                value={newMachine.manufacturer}
                                onChange={(e) => setNewMachine(prev => ({ ...prev, manufacturer: e.target.value }))}
                            />
                        </Grid>
                        <Grid item xs={12}>
                            <label className="form-label d-block bom-label mb-1">Machine Model <span className="text-danger">*</span></label>
                            <TextField
                                fullWidth
                                size="small"
                                placeholder="e.g. VCN-530C"
                                value={newMachine.model}
                                onChange={(e) => setNewMachine(prev => ({ ...prev, model: e.target.value }))}
                            />
                        </Grid>
                    </Grid>
                </DialogContent>
                <DialogActions sx={{ p: 2, borderTop: '1px solid #eee' }}>
                    <Button className='bom-btn-cancel' onClick={() => setMachineDialogOpen(false)} color="inherit">Cancel</Button>
                    <Button 
                        onClick={handleSaveNewMachine} 
                        className='bom-btn-save'
                        variant="contained"
                    >
                        Add Machine
                    </Button>
                </DialogActions>
            </Dialog>
        </div>
    );
};

export default CreateEnquiryPage;